import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon, obj = auth
  // Once the user performs an explicit auth action (login/register/logout),
  // a late-arriving background /auth/me must NOT overwrite that authoritative state.
  const settledByAction = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!settledByAction.current) setUser(data);
      return data;
    } catch (_) {
      if (!settledByAction.current) setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (phone, password, role) => {
    const { data } = await api.post("/auth/login", { phone, password, role });
    settledByAction.current = true;
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data; // does NOT log in — account is pending admin approval
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    settledByAction.current = true;
    setUser(null);
  };

  // If a logged-in customer is blocked/un-approved mid-session, the API returns
  // 403 ACCESS_DENIED. Force them out immediately.
  useEffect(() => {
    const onDenied = () => {
      settledByAction.current = true;
      setUser(null);
      api.post("/auth/logout").catch(() => {});
    };
    window.addEventListener("slm:access-denied", onDenied);
    return () => window.removeEventListener("slm:access-denied", onDenied);
  }, []);

  const applyUser = (data) => {
    settledByAction.current = true;
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, applyUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
